import { tokenize } from './fuzzyMatch';
import type { CompanyName } from './requests/account-lookup-requests';

/**
 * Picks out a company an admin names in a question — "failed payments for Acme
 * Trucking", "does Yardco have payouts on hold" — from the platform's company list.
 *
 * Whole words only, in order, ignoring case, punctuation and a trailing "Inc" or
 * "LLC". Unlike skill phrases this allows no typos: an answer scoped to the wrong
 * company reads as a confident fact about it, while a name that goes unrecognised
 * only leaves the answer platform-wide, which it says it is.
 */

const CORPORATE_SUFFIXES = new Set([
  'inc',
  'incorporated',
  'llc',
  'co',
  'corp',
  'corporation',
  'ltd',
  'limited',
  'company',
  'lp',
  'llp',
  'pllc',
]);

/**
 * Words a question is made of. A company whose whole name is made of these —
 * one called "Payments", or "Ready" — would otherwise be found in almost every
 * question about invoices.
 */
const QUESTION_WORDS = new Set(
  (
    'a an the any all some every each of for from by at to in on with without and or not no ' +
    'is are was were be been being has have had do does did done will would can could should ' +
    'i me my we us our you your they them their it its this that these those there here ' +
    'what which who whose how many much when where why whether show list find give get tell ' +
    'invoice invoices invoiced transaction transactions payment payments payout payouts ' +
    'charge charges refund refunds booking bookings account accounts customer customers ' +
    'buyer buyers supplier suppliers company companies platform ' +
    'failed fail failing failure failures declined decline disputed dispute disputes ' +
    'chargeback chargebacks hold held pending processing ready waiting awaiting due unpaid ' +
    'outstanding requested request requests denied rejected uncollectible manual manually ' +
    'paid pay open still right now currently today yesterday tomorrow week weeks month months ' +
    'year years quarter day days last past next this since ago so far recent recently new ' +
    'total totals amount amounts money owed owe charged approval approved transit progress way ' +
    'written write off offs bad debt please thanks ' +
    'january february march april may june july august september october november december ' +
    'jan feb mar apr jun jul aug sep sept oct nov dec'
  ).split(/\s+/),
);

/** Fewer letters than this is too little to be sure a name was meant. */
const MIN_NAME_LETTERS = 3;

/** A company's name as matchable words, without a trailing corporate suffix. */
export const companyNameWords = (name: string): string[] => {
  const words = tokenize(name);
  while (words.length > 1 && CORPORATE_SUFFIXES.has(words[words.length - 1])) words.pop();
  return words;
};

const containsRun = (tokens: string[], run: string[]): boolean => {
  for (let start = 0; start + run.length <= tokens.length; start += 1) {
    if (run.every((word, offset) => tokens[start + offset] === word)) return true;
  }
  return false;
};

/**
 * True when the message has words left over once the question's own words are
 * set aside — a name might be among them. False saves fetching every company on
 * the platform for a question that names none.
 */
export const mayNameCompany = (message: string): boolean =>
  tokenize(message).some((token) => !QUESTION_WORDS.has(token) && !/^\d+$/.test(token));

/**
 * The companies named in the message, longest name first. More than one comes
 * back only when several companies share the longest name found, which the
 * caller has to treat as ambiguous.
 */
export const findCompaniesInMessage = <T extends CompanyName>(message: string, companies: T[]): T[] => {
  const tokens = tokenize(message);
  let found: T[] = [];
  let longest = 0;

  for (const company of companies) {
    const words = companyNameWords(company.companyName ?? '');
    const letters = words.join('').length;
    if (letters < MIN_NAME_LETTERS) continue;
    if (words.every((word) => QUESTION_WORDS.has(word))) continue;
    if (!containsRun(tokens, words)) continue;

    if (letters > longest) {
      found = [company];
      longest = letters;
    } else if (letters === longest) {
      found.push(company);
    }
  }

  return found;
};
